"use client";

import { CSSProperties, JSX } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import {
  Legend,
  LegendIndex,
  LegendItem,
  LegendLabel,
  LegendStatus,
  LegendValue,
} from "@/components/charts/legend";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useLegendHoverSelect } from "@/lib/hooks/use-hover-select";
import { donutSlicePath, polarToCartesian } from "@/lib/svg-geometry";
import type { ModuleCycleData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import {
  cycleDemoData,
  phasesToLegendItems,
  type CyclePhase,
} from "./shared/cycles";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";

const tabular: CSSProperties = { fontVariantNumeric: "tabular-nums" };

export default function ModuleSegmentedCycle({
  data,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleCycleData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const phases = (data?.phases as CyclePhase[] | undefined) ?? cycleDemoData;

  const { hovered, selected, effective, setHovered, select, setLabelRef } =
    useLegendHoverSelect();
  const hoverGesture = useHoverGesture();

  const size = 320;
  const { svgRef, height: dynamicH } = useSvgContentHeight(size, [phases]);
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 118;
  const innerR = 76;
  const gapDeg = 2;
  const anglePerPhase = 360 / phases.length;

  const activePhase =
    effective !== null
      ? phases[effective]
      : phases.find((p) => p.status === "active") ?? phases[0];
  const doneCount = phases.filter((p) => p.status === "done").length;
  const legendItems = phasesToLegendItems(phases);

  return (
    <div
      className={cn("flex w-full justify-center", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="flex flex-col items-center gap-4 w-full max-w-[560px]">
        <div className="relative shrink-0">
          <svg
            ref={svgRef}
            width={size}
            height={dynamicH}
            viewBox={`0 0 ${size} ${dynamicH}`}
            style={{ overflow: "visible" }}
          >
            {phases.map((phase, i) => {
              const startAngle = i * anglePerPhase + gapDeg / 2;
              const endAngle = (i + 1) * anglePerPhase - gapDeg / 2;
              const midAngle = (startAngle + endAngle) / 2;
              const labelPos = polarToCartesian(cx, cy, outerR + 22, midAngle);
              const labelX = labelPos.x - 44;
              const labelY = labelPos.y - 14;
              const path = donutSlicePath(
                cx,
                cy,
                outerR,
                innerR,
                startAngle,
                endAngle,
              );

              const isDone = phase.status === "done";
              const isActive = phase.status === "active";
              const isHovered = effective === i;
              const dim = effective !== null && !isHovered;

              let fill: string;
              let fillOpacity: number;
              let stroke: string;
              let strokeOpacity: number;
              let strokeDasharray: string;

              if (isDone) {
                fill = "var(--status-2)"; fillOpacity = 0.65;
                stroke = "none"; strokeOpacity = 1; strokeDasharray = "0";
              } else if (isActive) {
                fill = "var(--status-1)"; fillOpacity = 1;
                stroke = "none"; strokeOpacity = 1; strokeDasharray = "0";
              } else {
                fill = "transparent"; fillOpacity = 1;
                stroke = "var(--status-neutral)"; strokeOpacity = 0.55; strokeDasharray = "3 3";
              }

              const activeAccentPath = isActive
                ? donutSlicePath(
                    cx,
                    cy,
                    outerR + 5,
                    outerR + 2,
                    startAngle,
                    endAngle,
                  )
                : null;

              return (
                <g
                  key={phase.id}
                  onPointerEnter={(e) => {
                    if (hoverGesture.enter(e)) setHovered(i);
                  }}
                  onPointerLeave={(e) => {
                    if (hoverGesture.leave(e)) setHovered(null);
                  }}
                  onClick={() => select(i, { kind: "segment" })}
                  style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                  opacity={dim ? 0.4 : 1}
                >
                  <path
                    d={path}
                    fill={fill}
                    fillOpacity={fillOpacity}
                    stroke={stroke}
                    strokeOpacity={strokeOpacity}
                    strokeWidth="1"
                    strokeDasharray={strokeDasharray}
                    strokeLinejoin="round"
                  />
                  {activeAccentPath && (
                    <path
                      d={activeAccentPath}
                      fill="var(--status-1)"
                      fillOpacity="0.9"
                      strokeLinejoin="round"
                    />
                  )}
                  <foreignObject
                    x={labelX}
                    y={labelY}
                    width={88}
                    height={32}
                    overflow="visible"
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: isActive || isHovered ? "var(--fg)" : "var(--muted)",
                        fontSize: "11px",
                        fontWeight: isActive ? 600 : 500,
                        lineHeight: 1.15,
                        textAlign: "center",
                        overflowWrap: "anywhere",
                        transition: "color 0.15s",
                      }}
                    >
                      {stripInlineMarkdown(phase.label)}
                    </div>
                  </foreignObject>
                </g>
              );
            })}

            <text x={cx} y={cy - 10} textAnchor="middle" fontSize="11" fill="var(--subtle)">
              {t("Currently")}
            </text>
            <foreignObject x={cx - 60} y={cy - 4} width={120} height={24} overflow="visible">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "var(--fg)",
                  fontSize: "17px",
                  fontWeight: 600,
                  lineHeight: 1.15,
                  textAlign: "center",
                  overflowWrap: "anywhere",
                }}
              >
                {stripInlineMarkdown(activePhase.label)}
              </div>
            </foreignObject>
            <text x={cx} y={cy + 28} textAnchor="middle" fontSize="11" fill="var(--muted)" style={tabular}>
              {doneCount} / {phases.length} done
            </text>
          </svg>
        </div>

        <SwipeScroll className="w-full pt-6 border-t border-dashed border-[var(--border-dashed)]">
          <Legend
            items={legendItems}
            hoveredIndex={hovered}
            onHoverChange={setHovered}
            selectedIndex={selected}
            onSelect={select}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 flex-col items-start gap-1 px-3 py-2">
              <div className="flex items-baseline gap-2">
                <LegendIndex />
                <LegendLabel className="text-sm" />
              </div>
              <div className="flex items-baseline gap-2">
                <LegendValue className="text-xs text-legend-muted-foreground tabular-nums" />
                <LegendStatus />
              </div>
            </LegendItem>
          </Legend>
        </SwipeScroll>
      </div>
    </div>
  );
}
